/** "14 days ago", "3 hours ago", "just now". */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const diffMs = now.getTime() - new Date(iso).getTime()
  const minutes = Math.round(diffMs / 60_000)

  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`

  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`

  // From the raw difference, not from `hours`: rounding twice turned 15d 15h
  // into "16 days ago".
  const days = Math.floor(diffMs / 86_400_000)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`

  const months = Math.floor(days / 30)
  return `${months} month${months === 1 ? '' : 's'} ago`
}

/** "07:52 11/08/2026" — matches the stamp used by the existing tool. */
export function absoluteTime(iso: string): string {
  const date = new Date(iso)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(date.getHours())}:${pad(date.getMinutes())} ${pad(date.getDate())}/${pad(
    date.getMonth() + 1,
  )}/${date.getFullYear()}`
}

/** "just now" / "42m" / "7h" / "3d" — compact, for dense table cells. */
export function minutesAgo(minutes: number): string {
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  if (minutes < 60 * 24) return `${Math.floor(minutes / 60)}h ago`
  return `${Math.floor(minutes / (60 * 24))}d ago`
}

/* Locale-aware formatters, built once. `Intl` rather than `$` + toFixed so the
 * same components read correctly wherever this ships — and so the thousands
 * separator never disagrees between a stat tile and the table under it. */
const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
})

const usdWhole = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

/** "$4.99". Pass `whole` for headline figures: "$339,366". */
export function money(amount: number, whole = false): string {
  return (whole ? usdWhole : usd).format(amount)
}

const decimal = new Intl.NumberFormat('en-US')

/** "18,402" */
export function count(value: number): string {
  return decimal.format(value)
}

/** "512 B" / "4.2 KB" / "1.8 MB". Nullish or zero renders as "—". */
export function fileSize(bytes: number | null | undefined): string {
  if (!bytes) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${unit === 0 ? value : value.toFixed(1)} ${units[unit]}`
}

/** "01 Aug 2026" — unambiguous in every locale, unlike 01/08/2026. */
export function calendarDate(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso))
}
